import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SHARED_IMPORTS } from '@shared/shared-providers';
import { CmUiDialogService } from '@cm/ui-modules';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  AutolabelExistingMatch,
  AutolabelGroup,
  AutolabelProposal,
  MediaAssetLabelSuggestion,
  MediaLabelAiService,
} from '@shared/services/media-label-ai.service';
import { LabelService } from '@shared/services/label.service';
import { MediaLibraryService, SnackBarService } from '@shared-services';
import { LabelChipComponent } from '@shared/components/label-chip/label-chip.component';
import { Label } from '@shared/models/label.model';

export interface LabelSuggestionDialogData {
  /** Review every suggestion of one analysis batch (toast action). */
  batchUuid?: string;
  /** Review the pending suggestions of one asset (badge in the media library). */
  mediaassetUuid?: string;
}

interface AssetInfo {
  name: string;
  thumbnailUrl: string | null;
}

const EXPANDED_STORAGE_KEY = 'highp_label_suggestion_dialog_expanded_groups';

/**
 * Human decision step of AI auto-labeling: per asset and per label group shows the existing
 * labels the model matched (already attached when above the threshold) and the NEW labels it
 * proposes. Accepting a proposal creates the label in its group and attaches it; the run is
 * resolved once no proposal is left undecided. Group expand/collapse state persists.
 */
@Component({
  selector: 'app-label-suggestion-dialog',
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    CommonModule,
    TranslateModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    LabelChipComponent,
  ],
  templateUrl: './label-suggestion-dialog.component.html',
  styleUrls: ['./label-suggestion-dialog.component.scss'],
})
export class LabelSuggestionDialogComponent implements OnInit {
  static open(
    dialog: CmUiDialogService,
    data: LabelSuggestionDialogData,
  ): DialogRef<boolean, LabelSuggestionDialogComponent> {
    return dialog.open<LabelSuggestionDialogComponent, LabelSuggestionDialogData, boolean>(
      LabelSuggestionDialogComponent,
      { width: '720px', maxWidth: '95vw', maxHeight: '90vh', data },
    );
  }

  private readonly data = inject<LabelSuggestionDialogData>(DIALOG_DATA);
  private readonly dialogRef = inject<DialogRef<boolean, LabelSuggestionDialogComponent>>(DialogRef);
  private readonly mediaLabelAi = inject(MediaLabelAiService);
  private readonly labelService = inject(LabelService);
  private readonly mediaLibraryService = inject(MediaLibraryService);
  private readonly snackBar = inject(SnackBarService);
  private readonly translate = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly suggestions = signal<MediaAssetLabelSuggestion[]>([]);
  readonly index = signal(0);
  readonly assets = signal<Record<string, AssetInfo>>({});
  readonly selectedExisting = signal<Set<string>>(new Set());
  readonly selectedProposals = signal<Set<string>>(new Set());
  readonly expanded = signal<Record<string, boolean>>(this.restoreExpanded());

  readonly current = computed<MediaAssetLabelSuggestion | null>(() => this.suggestions()[this.index()] ?? null);
  readonly total = computed(() => this.suggestions().length);
  readonly groups = computed<AutolabelGroup[]>(() => this.current()?.suggestions?.groups ?? []);
  readonly currentAsset = computed<AssetInfo | null>(() => {
    const uuid = this.current()?.mediaassetUuid;
    return uuid ? (this.assets()[uuid] ?? null) : null;
  });
  readonly isActionable = computed(() => this.current()?.status === 'DONE' && this.current()?.resolution == null);
  readonly hasPendingProposals = computed(() => (this.current()?.pendingProposals ?? 0) > 0);
  readonly selectionCount = computed(() => this.selectedExisting().size + this.selectedProposals().size);

  ngOnInit(): void {
    const source$ = this.data.batchUuid
      ? this.mediaLabelAi.getBatchSuggestions(this.data.batchUuid)
      : this.mediaLabelAi
          .search({ mediaassetUuid: this.data.mediaassetUuid, pendingOnly: true, size: 50 })
          .pipe(map((page) => page.content));

    source$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows) => {
        // Actionable rows first, then still-running and failed ones for transparency.
        const ordered = [...rows].sort((a, b) => this.rank(a) - this.rank(b));
        this.suggestions.set(ordered);
        this.index.set(0);
        this.preselect();
        this.loadAssetInfo(ordered.map((r) => r.mediaassetUuid));
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.snackBar.showWithText(this.translate.instant('ADMIN.medialibrary.aiLabels.dialog.loadError'), 'fail');
      },
    });
  }

  // ------------------------------------------------------------------ navigation

  previous(): void {
    if (this.index() > 0) {
      this.index.update((i) => i - 1);
      this.preselect();
    }
  }

  next(): void {
    if (this.index() < this.total() - 1) {
      this.index.update((i) => i + 1);
      this.preselect();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ------------------------------------------------------------------ selection

  proposalKey(group: AutolabelGroup, proposal: AutolabelProposal): string {
    return `${group.definitionUuid}::${proposal.name.toLowerCase()}`;
  }

  isExistingSelected(match: AutolabelExistingMatch): boolean {
    return this.selectedExisting().has(match.labelUuid);
  }

  toggleExisting(match: AutolabelExistingMatch): void {
    if (match.applied || match.rejected) return;
    this.selectedExisting.update((set) => this.toggled(set, match.labelUuid));
  }

  isProposalSelected(group: AutolabelGroup, proposal: AutolabelProposal): boolean {
    return this.selectedProposals().has(this.proposalKey(group, proposal));
  }

  toggleProposal(group: AutolabelGroup, proposal: AutolabelProposal): void {
    if (proposal.accepted || proposal.rejected) return;
    this.selectedProposals.update((set) => this.toggled(set, this.proposalKey(group, proposal)));
  }

  isExpanded(group: AutolabelGroup): boolean {
    return this.expanded()[group.definitionUuid] ?? true;
  }

  toggleGroup(group: AutolabelGroup): void {
    this.expanded.update((state) => {
      const next = { ...state, [group.definitionUuid]: !(state[group.definitionUuid] ?? true) };
      localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }

  // ------------------------------------------------------------------ decisions

  /** Attach the selected matches, create + attach the selected proposals; leave the rest undecided. */
  applySelected(): void {
    this.apply(false);
  }

  /** Same as applySelected, but every unselected proposal is rejected → the run resolves. */
  applyAndRejectRest(): void {
    this.apply(true);
  }

  rejectAll(): void {
    this.selectedExisting.set(new Set());
    this.selectedProposals.set(new Set());
    this.apply(true);
  }

  private apply(rejectRest: boolean): void {
    const suggestion = this.current();
    if (!suggestion || !this.isActionable() || this.saving()) return;
    const acceptProposals: { definitionUuid: string; name: string }[] = [];
    for (const group of this.groups()) {
      for (const proposal of group.proposed) {
        if (this.isProposalSelected(group, proposal)) {
          acceptProposals.push({ definitionUuid: group.definitionUuid, name: proposal.name });
        }
      }
    }
    this.saving.set(true);
    this.mediaLabelAi
      .apply(suggestion.uuid, {
        acceptExistingLabelUuids: [...this.selectedExisting()],
        acceptProposals,
        rejectRest,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.suggestions.update((rows) => rows.map((row) => (row.uuid === updated.uuid ? updated : row)));
          this.labelService.refresh$.next();
          this.mediaLibraryService.refresh$.next();
          this.snackBar.showWithText(this.translate.instant('ADMIN.medialibrary.aiLabels.dialog.applied'), 'success');
          this.saving.set(false);
          this.advanceOrClose();
        },
        error: () => {
          this.saving.set(false);
          this.snackBar.showWithText(this.translate.instant('ADMIN.medialibrary.aiLabels.dialog.applyError'), 'fail');
        },
      });
  }

  // ------------------------------------------------------------------ display helpers

  /** Minimal Label shape so app-label-chip renders a matched label like everywhere else. */
  chipFor(match: AutolabelExistingMatch, group: AutolabelGroup): Label {
    return {
      uuid: match.labelUuid,
      name: match.name,
      renderType: 'TAG',
      labelDefinition: { uuid: group.definitionUuid, name: group.definitionName, renderType: 'TAG' },
    } as unknown as Label;
  }

  percent(confidence: number): string {
    return `${Math.round((confidence ?? 0) * 100)}%`;
  }

  // ------------------------------------------------------------------ internals

  private rank(row: MediaAssetLabelSuggestion): number {
    if (row.status === 'DONE' && row.resolution == null && row.pendingProposals > 0) return 0;
    if (row.status === 'DONE' && row.resolution == null) return 1;
    if (row.status === 'PENDING') return 2;
    if (row.status === 'FAILED') return 3;
    return 4;
  }

  /**
   * Pre-selected: proposals, and matches auto-apply chose but could not attach (needsReview) — both
   * already passed the threshold. Other unapplied matches are optional extras and start unticked.
   */
  private preselect(): void {
    const proposals = new Set<string>();
    const existing = new Set<string>();
    for (const group of this.groups()) {
      for (const proposal of group.proposed) {
        if (!proposal.accepted && !proposal.rejected) proposals.add(this.proposalKey(group, proposal));
      }
      for (const match of group.existing) {
        if (match.needsReview && !match.applied && !match.rejected) existing.add(match.labelUuid);
      }
    }
    this.selectedProposals.set(proposals);
    this.selectedExisting.set(existing);
  }

  private advanceOrClose(): void {
    const remaining = this.suggestions().findIndex(
      (row, i) => i !== this.index() && row.status === 'DONE' && row.resolution == null && row.pendingProposals > 0,
    );
    if (remaining >= 0) {
      this.index.set(remaining);
      this.preselect();
    } else {
      this.dialogRef.close(true);
    }
  }

  private loadAssetInfo(uuids: string[]): void {
    const unique = [...new Set(uuids)];
    if (!unique.length) return;
    forkJoin(
      unique.map((uuid) =>
        this.http.get<{ name: string; thumbnailUrl: string | null }>(`/api/core/mediaassets/${uuid}`).pipe(
          // The API returns the stored file name; the library serves it under /mediaassets/ (nginx → highp-media).
          map((asset) => [uuid, { name: asset.name, thumbnailUrl: asset.thumbnailUrl ? `/mediaassets/${asset.thumbnailUrl}` : null }] as const),
          catchError(() => of([uuid, { name: uuid, thumbnailUrl: null }] as const)),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((entries) => this.assets.set(Object.fromEntries(entries)));
  }

  private toggled(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  }

  private restoreExpanded(): Record<string, boolean> {
    try {
      return JSON.parse(localStorage.getItem(EXPANDED_STORAGE_KEY) ?? '{}');
    } catch {
      return {};
    }
  }
}
